/* Lebanizi Theme JS — cart drawer, header, accordions, gallery */
'use strict';

/* ── Helpers ── */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const money = cents => '$' + (cents / 100).toFixed(2);

/* ── Cart state ── */
const Cart = {
  _count: 0,
  _subtotal: 0,

  async fetch() {
    const res = await fetch('/cart.js');
    return res.json();
  },

  async add(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: variantId, quantity }),
    });
    if (!res.ok) throw new Error('Add to cart failed');
    return res.json();
  },

  async remove(key) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
    return res.json();
  },

  async change(key, qty) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity: qty }),
    });
    return res.json();
  },
};

/* ── Cart Drawer (React) ── */
function CartDrawer() {
  const React = window.React;
  const { useState, useEffect, useCallback } = React;

  const [items, setItems] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    const data = await Cart.fetch();
    setItems(data.items || []);
    setSubtotal(data.total_price || 0);
    updateCountBadge(data.item_count || 0);
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener('lz:cart:open', refresh);
    return () => window.removeEventListener('lz:cart:open', refresh);
  }, [refresh]);

  const handleRemove = async (key) => {
    setLoading(true);
    await Cart.remove(key);
    await refresh();
    setLoading(false);
  };

  const e = React.createElement;

  if (items.length === 0) {
    return e('div', { className: 'lz-cart-empty' },
      e('div', { className: 'lz-cart-empty__icon' }, '🛒'),
      e('div', { className: 'lz-cart-empty__msg' }, 'Your cart is empty'),
      e('button', {
        className: 'lz-btn lz-btn--green',
        style: { marginTop: '18px' },
        onClick: () => closeCartDrawer(),
      }, 'Start shopping')
    );
  }

  return e(React.Fragment, null,
    items.map(item =>
      e('div', { key: item.key, className: 'lz-cart-item' },
        e('div', {
          className: 'lz-cart-item__thumb',
          style: { background: '#F4F1E7' },
        },
          item.image
            ? e('img', { src: item.image, alt: item.title, width: 58, height: 58, style: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' } })
            : e('span', { style: { fontSize: '24px' } }, '📦')
        ),
        e('div', { className: 'lz-cart-item__info' },
          e('div', { className: 'lz-cart-item__title' }, item.product_title),
          item.variant_title && item.variant_title !== 'Default Title'
            ? e('div', { style: { fontSize: '12px', color: '#9aa890', fontWeight: 700 } }, item.variant_title)
            : null,
          e('div', { className: 'lz-cart-item__meta' }, `Qty ${item.quantity} · ${money(item.line_price)}`)
        ),
        e('button', {
          className: 'lz-cart-item__remove',
          onClick: () => handleRemove(item.key),
          disabled: loading,
          'aria-label': `Remove ${item.product_title}`,
        }, 'Remove')
      )
    )
  );
}

/* ── Mount React cart items ── */
function mountCartDrawer() {
  if (!window.React || !window.ReactDOM) return;
  const root = $('#lz-cart-items');
  if (!root) return;
  const reactRoot = window.ReactDOM.createRoot(root);

  const rerenderCart = async () => {
    const data = await Cart.fetch();
    updateCountBadge(data.item_count || 0);
    updateSubtotal(data.total_price || 0, data.item_count || 0);

    reactRoot.render(window.React.createElement(CartItems, { initialItems: data.items, initialSubtotal: data.total_price }));
  };

  window.addEventListener('lz:cart:open', rerenderCart);
  rerenderCart();
}

/* Simple non-React cart items renderer for reliability */
function renderCartItems(items, subtotal) {
  const root = $('#lz-cart-items');
  if (!root) return;

  if (!items || items.length === 0) {
    root.innerHTML = `
      <div class="lz-cart-empty">
        <div class="lz-cart-empty__icon">🛒</div>
        <div class="lz-cart-empty__msg">Your cart is empty</div>
        <button class="lz-btn lz-btn--green" style="margin-top:18px;" onclick="closeCartDrawer()">Start shopping</button>
      </div>`;
    $('#lz-cart-footer').hidden = true;
    return;
  }

  root.innerHTML = items.map(item => `
    <div class="lz-cart-item">
      <div class="lz-cart-item__thumb" style="background:#F4F1E7;">
        ${item.image
          ? `<img src="${item.image}" alt="${escHtml(item.product_title)}" width="58" height="58" style="width:100%;height:100%;object-fit:cover;border-radius:12px;">`
          : '<span style="font-size:24px;">📦</span>'}
      </div>
      <div class="lz-cart-item__info">
        <div class="lz-cart-item__title">${escHtml(item.product_title)}</div>
        ${item.variant_title && item.variant_title !== 'Default Title' ? `<div style="font-size:12px;color:#9aa890;font-weight:700;">${escHtml(item.variant_title)}</div>` : ''}
        <div class="lz-cart-item__meta">Qty ${item.quantity} · ${money(item.line_price)}</div>
      </div>
      <button class="lz-cart-item__remove" data-key="${item.key}" aria-label="Remove ${escHtml(item.product_title)}">Remove</button>
    </div>`).join('');

  $$('.lz-cart-item__remove', root).forEach(btn => {
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      await Cart.remove(btn.dataset.key);
      await refreshCartDrawer();
    });
  });

  updateSubtotal(subtotal, items.length);
}

function updateSubtotal(subtotal, count) {
  const footer = $('#lz-cart-footer');
  const el = $('#lz-cart-subtotal');
  if (!footer || !el) return;
  footer.hidden = count === 0;
  el.textContent = money(subtotal);
}

function updateCountBadge(count) {
  const badge = $('#lz-cart-count');
  if (badge) badge.textContent = count;
  Cart._count = count;
}

async function refreshCartDrawer() {
  const data = await Cart.fetch();
  renderCartItems(data.items, data.total_price);
  updateCountBadge(data.item_count || 0);
}

function escHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/* ── Drawer open/close ── */
function openCartDrawer() {
  const drawer  = $('#lz-cart-drawer');
  const overlay = $('#lz-cart-overlay');
  const openBtn = $('#lz-cart-open');
  if (!drawer) return;
  drawer.classList.add('is-open');
  overlay.classList.add('is-open');
  drawer.removeAttribute('aria-hidden');
  if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
  document.body.style.overflow = 'hidden';
  window.dispatchEvent(new CustomEvent('lz:cart:open'));
}

function closeCartDrawer() {
  const drawer  = $('#lz-cart-drawer');
  const overlay = $('#lz-cart-overlay');
  const openBtn = $('#lz-cart-open');
  if (!drawer) return;
  drawer.classList.remove('is-open');
  overlay.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
  document.body.style.overflow = '';
}

window.closeCartDrawer = closeCartDrawer;

/* ── Add to cart buttons ── */
function initAddToCart() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.lz-add-to-cart');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    const original = btn.textContent;
    btn.textContent = '✓ Added';
    btn.disabled = true;
    try {
      await Cart.add(parseInt(variantId, 10), 1);
      await refreshCartDrawer();
      openCartDrawer();
    } catch (err) {
      btn.textContent = 'Error';
    }
    setTimeout(() => {
      btn.textContent = original;
      btn.disabled = false;
    }, 1800);
  });
}

/* ── Product page ATC ── */
function initProductPage() {
  const atcBtn = $('#lz-atc-btn');
  const qtyNum = $('#lz-qty-num');
  const qtyDec = $('#lz-qty-dec');
  const qtyInc = $('#lz-qty-inc');
  if (!atcBtn) return;

  let qty = 1;

  const updatePrice = () => {
    const cents = parseInt(atcBtn.dataset.price, 10);
    atcBtn.textContent = `PREORDER NOW · ${money(cents * qty)}`;
  };

  qtyDec && qtyDec.addEventListener('click', () => {
    if (qty > 1) qty--;
    if (qtyNum) qtyNum.textContent = qty;
    updatePrice();
  });
  qtyInc && qtyInc.addEventListener('click', () => {
    qty++;
    if (qtyNum) qtyNum.textContent = qty;
    updatePrice();
  });

  if (!$('.lz-bundle-form-wrapper[data-bundle]')) {
    atcBtn.addEventListener('click', async () => {
      const variantId = atcBtn.dataset.variantId;
      if (!variantId) return;
      const original = atcBtn.textContent;
      atcBtn.textContent = 'Adding…';
      atcBtn.disabled = true;
      try {
        await Cart.add(parseInt(variantId, 10), qty);
        await refreshCartDrawer();
        openCartDrawer();
      } catch {
        atcBtn.textContent = 'Error — try again';
      }
      setTimeout(() => {
        atcBtn.textContent = original;
        atcBtn.disabled = false;
      }, 1800);
    });
  }

  /* Variant selector */
  const variantDataEl = $('#lz-product-variants-json');
  if (variantDataEl) {
    const variants = JSON.parse(variantDataEl.textContent);
    const selectedOptions = {};
    const priceEl   = $('#lz-product-price');
    const compareEl = $('#lz-product-compare-price');
    const saleBadge = document.querySelector('.lz-sale-badge');

    const updateVariantDisplay = (variant) => {
      if (!variant) return;
      atcBtn.dataset.variantId = variant.id;
      atcBtn.dataset.price = variant.price;
      qty = 1;
      if (qtyNum) qtyNum.textContent = 1;

      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (priceEl) {
        priceEl.textContent = money(variant.price);
        priceEl.classList.toggle('lz-product__price--sale', onSale);
      }
      if (compareEl) {
        if (onSale) {
          compareEl.textContent = money(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.textContent = '';
          compareEl.style.display = 'none';
        }
      }
      if (saleBadge) saleBadge.style.display = onSale ? '' : 'none';

      if (variant.available) {
        atcBtn.disabled = false;
        updatePrice();
      } else {
        atcBtn.disabled = true;
        atcBtn.textContent = 'Sold out';
      }
    };

    // Cascade rule: a button at position N is only constrained by selections
    // at positions *lower* than N, never by positions higher than N.
    // This means colour buttons are never disabled because of a size selection.
    const refreshButtonAvailability = () => {
      $$('.lz-variant-btn').forEach(btn => {
        const btnPos = parseInt(btn.dataset.option, 10);
        const btnVal = btn.dataset.value;
        const hasAvailable = variants.some(v =>
          v.available && v.options.every((opt, i) => {
            const p = i + 1;
            if (p === btnPos) return opt === btnVal;
            if (p < btnPos)   return selectedOptions[p] === undefined || selectedOptions[p] === opt;
            return true; // positions higher than btnPos are ignored
          })
        );
        btn.disabled = !hasAvailable;
      });
    };

    const setButtonPressed = (pos, val) => {
      $$('.lz-variant-btn[data-option="' + pos + '"]').forEach(b => {
        b.setAttribute('aria-pressed', b.dataset.value === val ? 'true' : 'false');
      });
    };

    // Seed selectedOptions from the variant Liquid already resolved
    const initVariantId = parseInt(atcBtn.dataset.variantId, 10);
    const initVariant = variants.find(v => v.id === initVariantId)
      || variants.find(v => v.available)
      || variants[0];
    if (initVariant) {
      initVariant.options.forEach((opt, i) => { selectedOptions[i + 1] = opt; });
    }

    $$('.lz-variant-btn').forEach(btn => {
      const pos = parseInt(btn.dataset.option, 10);
      const val = btn.dataset.value;
      btn.setAttribute('aria-pressed', selectedOptions[pos] === val ? 'true' : 'false');

      btn.addEventListener('click', () => {
        selectedOptions[pos] = val;
        setButtonPressed(pos, val);

        // Try exact match across all options
        let match = variants.find(v => v.options.every((opt, i) => selectedOptions[i + 1] === opt));

        // No exact match (e.g. switched colour but current size doesn't exist in new colour):
        // auto-pick the first available variant for the clicked option value and
        // update the other option selections to match it.
        if (!match) {
          match = variants.find(v => v.available && v.options[pos - 1] === val);
          if (match) {
            match.options.forEach((opt, i) => {
              const p = i + 1;
              if (p !== pos) {
                selectedOptions[p] = opt;
                setButtonPressed(p, opt);
              }
            });
          }
        }

        refreshButtonAvailability();
        if (match) updateVariantDisplay(match);
      });
    });

    refreshButtonAvailability();
    if (initVariant) updateVariantDisplay(initVariant);
  }

  /* Thumbnail carousel arrows */
  const thumbsStrip = document.getElementById('lz-thumbs-strip');
  const thumbsPrev  = document.querySelector('.lz-product__thumbs-arrow--prev');
  const thumbsNext  = document.querySelector('.lz-product__thumbs-arrow--next');
  if (thumbsStrip && (thumbsPrev || thumbsNext)) {
    const scrollAmount = () => thumbsStrip.clientWidth * 0.70;
    if (thumbsPrev) thumbsPrev.addEventListener('click', () => thumbsStrip.scrollBy({ left: -scrollAmount(), behavior: 'smooth' }));
    if (thumbsNext) thumbsNext.addEventListener('click', () => thumbsStrip.scrollBy({ left:  scrollAmount(), behavior: 'smooth' }));
    const syncArrows = () => {
      if (thumbsPrev) thumbsPrev.hidden = thumbsStrip.scrollLeft < 2;
      if (thumbsNext) thumbsNext.hidden = thumbsStrip.scrollLeft + thumbsStrip.clientWidth >= thumbsStrip.scrollWidth - 2;
    };
    thumbsStrip.addEventListener('scroll', syncArrows, { passive: true });
    syncArrows();
  }

  /* Thumbnail gallery */
  const galleryMain = $('#lz-gallery-main');
  const originalGalleryHTML = galleryMain ? galleryMain.innerHTML : null;

  $$('.lz-product__thumb').forEach((thumb, index) => {
    thumb.addEventListener('click', () => {
      const src = thumb.dataset.src;
      if (galleryMain && src) {
        const mainImg = $('#lz-main-img');
        if (mainImg) {
          mainImg.src = src;
        } else {
          // box-set or other non-img main display — swap to photo then back
          if (index === 0 && originalGalleryHTML) {
            galleryMain.innerHTML = originalGalleryHTML;
          } else {
            const img = document.createElement('img');
            img.id = 'lz-main-img';
            img.src = src;
            img.style.cssText = 'width:100%;height:auto;border-radius:12px;';
            img.width = 600;
            img.height = 600;
            galleryMain.innerHTML = '';
            galleryMain.appendChild(img);
          }
        }
      }
      $$('.lz-product__thumb').forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });
}

/* ── Accordions ── */
function initAccordions() {
  $$('.lz-accordion__trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!expanded));
      const body = trigger.nextElementSibling;
      const sign = trigger.querySelector('.lz-accordion__sign');
      if (body)  body.classList.toggle('is-open', !expanded);
      if (sign)  sign.textContent = expanded ? '+' : '–';
    });
  });
}

/* ── Mobile nav ── */
function initMobileNav() {
  const toggle = $('#lz-menu-toggle');
  const nav    = $('#lz-mobile-nav');
  if (!toggle || !nav) return;
  toggle.addEventListener('click', () => {
    const open = nav.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', String(open));
  });
}

/* ── Password page toggle ── */
function initPasswordPage() {
  const toggle = $('#lz-pw-toggle');
  const access = $('#lz-pw-access');
  if (!toggle || !access) return;
  toggle.addEventListener('click', () => {
    const hidden = access.hidden;
    access.hidden = !hidden;
    toggle.setAttribute('aria-expanded', String(hidden));
  });
}

/* ── Cart open/close wiring ── */
function initCartDrawer() {
  const openBtn  = $('#lz-cart-open');
  const closeBtn = $('#lz-cart-close');
  const overlay  = $('#lz-cart-overlay');

  openBtn  && openBtn.addEventListener('click', openCartDrawer);
  closeBtn && closeBtn.addEventListener('click', closeCartDrawer);
  overlay  && overlay.addEventListener('click', closeCartDrawer);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeCartDrawer();
  });
}

/* ── Header group sections file ── */
/* (sections/header-group is a groups file — wired in layout) */

/* ── Hero box rotator ── */
function initBoxRotator() {
  const wrap = $('[data-box-rotate]');
  if (!wrap) return;

  const slides = $$('.lz-box-slide', wrap);
  const dots   = $$('.lz-box-dot',   wrap);
  if (slides.length < 2) return;

  const interval = parseInt(wrap.dataset.interval, 10) || 4000;
  const doPause  = wrap.dataset.pause === 'true';
  let current = 0;
  let timer;

  function goTo(idx) {
    idx = (idx + slides.length) % slides.length;
    slides[current].classList.remove('is-active');
    dots.forEach((d, i) => {
      d.classList.toggle('is-active', i === idx);
      d.setAttribute('aria-selected', String(i === idx));
    });
    current = idx;
    slides[current].classList.add('is-active');
  }

  function startTimer() {
    clearInterval(timer);
    timer = setInterval(() => goTo(current + 1), interval);
  }

  startTimer();

  if (doPause) {
    wrap.addEventListener('mouseenter', () => clearInterval(timer));
    wrap.addEventListener('mouseleave', startTimer);
  }

  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => { goTo(i); startTimer(); });
  });
}

/* ── Bundle page: per-component variant selection ── */
function initBundlePage() {
  const atcBtn = $('#lz-atc-btn');
  if (!atcBtn || !$('.lz-bundle-form-wrapper[data-bundle]')) return;

  const qtyNum = $('#lz-qty-num');

  // Track selected variant id per component index
  const selectedVariantIds = {};

  $$('[data-bundle-component]').forEach(comp => {
    const idx    = comp.dataset.bundleComponent;
    const input  = comp.querySelector('.lz-bundle-component-variant-id');
    const jsonEl = comp.querySelector('.lz-bundle-component-variants-json');
    if (!input || !jsonEl) return;

    const variants = JSON.parse(jsonEl.textContent);
    const selectedOptions = {};

    // Seed from first available variant
    const seed = variants.find(v => v.available) || variants[0];
    if (seed) {
      seed.options.forEach((opt, i) => { selectedOptions[i + 1] = opt; });
      input.value = seed.id;
    }
    selectedVariantIds[idx] = parseInt(input.value, 10);

    const btnsInComp = pos => $$('.lz-bundle-variant-btn[data-option="' + pos + '"]', comp);

    const refreshAvailability = () => {
      $$('.lz-bundle-variant-btn', comp).forEach(btn => {
        const pos = parseInt(btn.dataset.option, 10);
        const val = btn.dataset.value;
        btn.disabled = !variants.some(v =>
          v.available && v.options.every((opt, i) => {
            const p = i + 1;
            if (p === pos) return opt === val;
            if (p < pos)   return selectedOptions[p] === undefined || selectedOptions[p] === opt;
            return true;
          })
        );
      });
    };

    $$('.lz-bundle-variant-btn', comp).forEach(btn => {
      const pos = parseInt(btn.dataset.option, 10);
      const val = btn.dataset.value;
      btn.setAttribute('aria-pressed', selectedOptions[pos] === val ? 'true' : 'false');

      btn.addEventListener('click', () => {
        selectedOptions[pos] = val;
        btnsInComp(pos).forEach(b => b.setAttribute('aria-pressed', b.dataset.value === val ? 'true' : 'false'));

        let match = variants.find(v => v.options.every((opt, i) => selectedOptions[i + 1] === opt));
        if (!match) {
          match = variants.find(v => v.available && v.options[pos - 1] === val);
          if (match) {
            match.options.forEach((opt, i) => {
              const p = i + 1;
              if (p !== pos) {
                selectedOptions[p] = opt;
                btnsInComp(p).forEach(b => b.setAttribute('aria-pressed', b.dataset.value === opt ? 'true' : 'false'));
              }
            });
          }
        }

        refreshAvailability();
        if (match) {
          input.value = match.id;
          selectedVariantIds[idx] = match.id;
        }
      });
    });

    refreshAvailability();
  });

  atcBtn.addEventListener('click', async () => {
    const variantId = parseInt(atcBtn.dataset.variantId, 10);
    if (!variantId) return;
    const qty = parseInt(qtyNum?.textContent || '1', 10);

    // Build readable line item properties for each component's selection
    const properties = {};
    $$('[data-bundle-component]').forEach(comp => {
      const idx      = comp.dataset.bundleComponent;
      const input    = comp.querySelector('.lz-bundle-component-variant-id');
      const jsonEl   = comp.querySelector('.lz-bundle-component-variants-json');
      if (!input || !jsonEl) return;
      const variants    = JSON.parse(jsonEl.textContent);
      const optionNames = JSON.parse(comp.dataset.optionNames || '[]');
      const selId       = parseInt(input.value, 10);
      const variant     = variants.find(v => v.id === selId);
      if (!variant) return;
      const title = comp.dataset.title || `Item ${parseInt(idx, 10) + 1}`;
      optionNames.forEach((name, i) => {
        const val = variant[`option${i + 1}`];
        if (val && val !== 'Default Title') {
          properties[`${title} — ${name}`] = val;
        }
      });
    });

    const original = atcBtn.textContent;
    atcBtn.textContent = 'Adding…';
    atcBtn.disabled = true;
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: variantId, quantity: qty, properties }),
      });
      await refreshCartDrawer();
      openCartDrawer();
    } catch {
      atcBtn.textContent = 'Error — try again';
    }
    setTimeout(() => {
      atcBtn.textContent = original;
      atcBtn.disabled = false;
    }, 1800);
  });
}

/* ── Cart page quantity buttons ── */
function initCartPage() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.lz-ajax-qty');
    if (!btn) return;
    e.preventDefault();
    const item = btn.closest('.lz-cart__item');
    const numEl = item && $('.lz-cart__qty-num', item);
    const current = parseInt(numEl ? numEl.textContent : '0', 10) || 0;
    const next = Math.max(0, current + parseInt(btn.dataset.change, 10));
    $$('.lz-ajax-qty', item).forEach(b => { b.disabled = true; });
    if (item) item.style.opacity = '0.5';
    try {
      await Cart.change(btn.dataset.key, next);
    } finally {
      // Reload so totals, discounts and the shipping bar re-render server-side
      window.location.reload();
    }
  });
}

/* ── Bootstrap ── */
document.addEventListener('DOMContentLoaded', () => {
  initCartDrawer();
  initAddToCart();
  initCartPage();
  initProductPage();
  initAccordions();
  initMobileNav();
  initPasswordPage();
  refreshCartDrawer();
  initBoxRotator();
  initBundlePage();
});
